<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
	name: 'CButton'
});
</script>
<template>
	<component
					:is="tag"
					ref="_ref"
					:class="_classNames"
					v-bind="_props"
					@click="handleClick"
	>
		<template v-if="loading">
			<slot v-if="$slots.loading" name="loading" />
			<c-icon v-else class="is-loading">
				<component :is="loadingIcon" />
			</c-icon>
		</template>
		<c-icon v-else-if="icon || $slots.icon">
			<component v-if="icon" :is="icon" />
			<slot v-else name="icon" />
		</c-icon>
		<span v-if="$slots.default">
			<slot />
		</span>
		<c-icon v-if="rightIcon || $slots.rightIcon">
			<component v-if="rightIcon" :is="rightIcon" />
			<slot v-else name="rightIcon" />
		</c-icon>
	</component>
</template>

<script setup lang="ts">
import { buttonEmits, buttonProps } from "../model/defaults.ts";
import { useButton } from "./useButton.ts";
import { CIcon } from "@/shared/ui/icon";



const props = defineProps(buttonProps)
const emit = defineEmits(buttonEmits)

const {
	_ref,
	_props,
	_classNames,
	tag,
	icon,
	rightIcon,
	loading,
	loadingIcon,
	handleClick
} = useButton(props, emit)

defineExpose({
	/** @description button html element */
	ref: _ref
})
</script>

<style lang="scss">
// Размеры
%button_size_L {
	--button-font-size: 15px;
	--button-line-height: 20px;
	--button-height: var(--size-large);
	--button-border-radius: var(--border-radius-base);
	--button-padding-vertical: var(--padding-vertical-large);
	--button-padding-horizontal: var(--padding-horizontal-large);
	--button-horizontal-gap: 8px;
}
%button_size_M {
	--button-font-size: var(--font-size-base);
	--button-line-height: 20px;
	--button-height: var(--size-medium);
	--button-border-radius: var(--border-radius-base);
	--button-padding-vertical: var(--padding-vertical-medium);
	--button-padding-horizontal: var(--padding-horizontal-medium);
	--button-horizontal-gap: 8px;
}
%button_size_S {
	--button-font-size: var(--font-size-extra-small);
	--button-line-height: 16px;
	--button-height: var(--size-small);
	--button-border-radius: var(--border-radius-base);
	--button-padding-vertical: var(--padding-vertical-base);
	--button-padding-horizontal: var(--padding-horizontal-base);
	--button-horizontal-gap: 4px;
}
%button_size_XS {
	--button-font-size: var(--font-size-extra-small);
	--button-line-height: 16px;
	--button-height: var(--size-extra-small);
	--button-border-radius: var(--border-radius-small);
	--button-padding-vertical: var(--padding-vertical-small);
	--button-padding-horizontal: var(--padding-horizontal-small);
	--button-horizontal-gap: 4px;
}
%button_size_XXS {
	--button-font-size: var(--font-size-extra-small);
	--button-line-height: 16px;
	--button-height: var(--size-extra-extra-small);
	--button-border-radius: var(--border-radius-small);
	--button-padding-vertical: var(--padding-vertical-extra-small);
	--button-padding-horizontal: var(--padding-horizontal-extra-small);
	--button-horizontal-gap: 4px;
}

// Стилистика
%button_style_filled {
	background-color: var(--button-background-color-default);
	color: var(--button-text-color-default);
	border: 1px solid var(--button-background-color-default);

	&:hover, &:focus {
		background-color: var(--button-background-color-hover);
		color: var(--button-text-color-hover);
		border-color: var(--button-background-color-hover);
	}

	&:active {
		background-color: var(--button-background-color-active);
		color: var(--button-text-color-active);
		border-color: var(--button-background-color-active);
	}

	&:disabled,
	&[disabled],
	&.is-disabled {
		background-color: var(--button-background-color-disabled);
		color: var(--button-text-color-disabled);
		border-color: var(--button-background-color-disabled);
	}
}
%button_style_bordered {
	background-color: var(--button-background-color-default);
	color: var(--button-text-color-default);
	border: 1px solid var(--button-border-color-default);

	&:hover, &:focus {
		background-color: var(--button-background-color-hover);
		color: var(--button-text-color-hover);
		border-color: var(--button-border-color-hover);
	}

	&:active {
		background-color: var(--button-background-color-active);
		color: var(--button-text-color-active);
		border-color: var(--button-border-color-active);
	}

	&:disabled,
	&[disabled],
	&.is-disabled {
		background-color: var(--button-background-color-disabled);
		color: var(--button-text-color-disabled);
		border-color: var(--button-border-color-disabled);
	}
}
%button_style_text {
	background-color: var(--button-background-color-default);
	color: var(--button-text-color-default);
	border: 1px solid var(--button-background-color-default);

	&:hover, &:focus {
		background-color: var(--button-background-color-hover);
		color: var(--button-text-color-hover);
		border-color: var(--button-background-color-hover);
	}

	&:active {
		background-color: var(--button-background-color-active);
		color: var(--button-text-color-active);
		border-color: var(--button-background-color-active);
	}

	&:disabled,
	&[disabled],
	&.is-disabled {
		background-color: var(--button-background-color-disabled);
		color: var(--button-text-color-disabled);
		border-color: var(--button-background-color-disabled);
	}
}

// Семантика
// Акцент
%button_filled_accent_primary {
	--button-outline-color: var(--accent-color_4);

	--button-background-color-default: var(--accent-color_1);
	--button-text-color-default: var(--color-white);
	--button-border-color-default: var(--accent-color_2);

	--button-background-color-hover: var(--accent-color_0);
	--button-text-color-hover: var(--color-white);
	--button-border-color-hover: var(--accent-color_3);

	--button-background-color-active: var(--accent-color_-1);
	--button-text-color-active: var(--color-white);
	--button-border-color-active: var(--accent-color_1);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: var(--accent-color_4);
	--button-text-color-disabled: var(--global-white);
	--button-border-color-disabled: var(--accent-color_6);
}
%button_bordered_accent_primary {
	--button-outline-color: var(--accent-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--accent-color_0);
	--button-border-color-default: var(--accent-color_5);

	--button-background-color-hover: var(--accent-color_10);
	--button-text-color-hover: var(--accent-color_0);
	--button-border-color-hover: var(--accent-color_3);

	--button-background-color-active: var(--accent-color_8);
	--button-text-color-active: var(--accent-color_-1);
	--button-border-color-active: var(--accent-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--accent-color_5);
	--button-border-color-disabled: var(--accent-color_8);
}
%button_text_accent_primary {
	--button-outline-color: var(--accent-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--accent-color_0);
	--button-border-color-default: var(--accent-color_5);

	--button-background-color-hover: var(--accent-color_10);
	--button-text-color-hover: var(--accent-color_0);
	--button-border-color-hover: var(--accent-color_3);

	--button-background-color-active: var(--accent-color_8);
	--button-text-color-active: var(--accent-color_-1);
	--button-border-color-active: var(--accent-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--accent-color_5);
	--button-border-color-disabled: var(--accent-color_8);
}

%button_filled_accent_secondary {
	--button-outline-color: var(--accent-color_4);

	--button-background-color-default: var(--accent-color_8);
	--button-text-color-default: var(--accent-color_1);
	--button-border-color-default: var(--accent-color_4);

	--button-background-color-hover: var(--accent-color_7);
	--button-text-color-hover: var(--accent-color_1);
	--button-border-color-hover: var(--accent-color_3);

	--button-background-color-active: var(--accent-color_6);
	--button-text-color-active: var(--accent-color_0);
	--button-border-color-active: var(--accent-color_2);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: var(--accent-color_10);
	--button-text-color-disabled: var(--accent-color_4);
	--button-border-color-disabled: var(--accent-color_6);
}
%button_bordered_accent_secondary {
	--button-outline-color: var(--accent-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--accent-color_2);
	--button-border-color-default: var(--accent-color_5);

	--button-background-color-hover: var(--accent-color_10);
	--button-text-color-hover: var(--accent-color_2);
	--button-border-color-hover: var(--accent-color_3);

	--button-background-color-active: var(--accent-color_8);
	--button-text-color-active: var(--accent-color_0);
	--button-border-color-active: var(--accent-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--accent-color_5);
	--button-border-color-disabled: var(--accent-color_8);
}
%button_text_accent_secondary {
	--button-outline-color: var(--accent-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--accent-color_2);
	--button-border-color-default: var(--accent-color_5);

	--button-background-color-hover: var(--accent-color_10);
	--button-text-color-hover: var(--accent-color_2);
	--button-border-color-hover: var(--accent-color_3);

	--button-background-color-active: var(--accent-color_8);
	--button-text-color-active: var(--accent-color_0);
	--button-border-color-active: var(--accent-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--accent-color_5);
	--button-border-color-disabled: var(--accent-color_8);
}

// Стандарт
%button_filled_default_primary {
	--button-outline-color: var(--neutral-color_4);

	--button-background-color-default: var(--neutral-color_-2);
	--button-text-color-default: var(--color-white);
	--button-border-color-default: var(--neutral-color_0);

	--button-background-color-hover: var(--neutral-color_-3);
	--button-text-color-hover: var(--color-white);
	--button-border-color-hover: var(--neutral-color_-1);

	--button-background-color-active: var(--neutral-color_-4);
	--button-text-color-active: var(--color-white);
	--button-border-color-active: var(--neutral-color_-2);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: var(--neutral-color_2);
	--button-text-color-disabled: var(--global-white);
	--button-border-color-disabled: var(--neutral-color_1);
}
%button_bordered_default_primary {
	--button-outline-color: var(--neutral-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--neutral-color_-4);
	--button-border-color-default: var(--neutral-color_5);

	--button-background-color-hover: var(--neutral-color_10);
	--button-text-color-hover: var(--neutral-color_-4);
	--button-border-color-hover: var(--neutral-color_3);

	--button-background-color-active: var(--neutral-color_8);
	--button-text-color-active: var(--neutral-color_-4);
	--button-border-color-active: var(--neutral-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--neutral-color_4);
	--button-border-color-disabled: var(--neutral-color_8);
}
%button_text_default_primary {
	--button-outline-color: var(--neutral-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--neutral-color_-4);
	--button-border-color-default: var(--neutral-color_5);

	--button-background-color-hover: var(--neutral-color_10);
	--button-text-color-hover: var(--neutral-color_-4);
	--button-border-color-hover: var(--neutral-color_3);

	--button-background-color-active: var(--neutral-color_8);
	--button-text-color-active: var(--neutral-color_-4);
	--button-border-color-active: var(--neutral-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--neutral-color_4);
	--button-border-color-disabled: var(--neutral-color_8);
}

%button_filled_default_secondary {
	--button-outline-color: var(--neutral-color_4);

	--button-background-color-default: var(--neutral-color_8);
	--button-text-color-default: var(--neutral-color_1);
	--button-border-color-default: var(--neutral-color_4);

	--button-background-color-hover: var(--neutral-color_7);
	--button-text-color-hover: var(--neutral-color_1);
	--button-border-color-hover: var(--neutral-color_3);

	--button-background-color-active: var(--neutral-color_6);
	--button-text-color-active: var(--neutral-color_0);
	--button-border-color-active: var(--neutral-color_2);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: var(--neutral-color_10);
	--button-text-color-disabled: var(--neutral-color_4);
	--button-border-color-disabled: var(--neutral-color_6);
}
%button_bordered_default_secondary {
	--button-outline-color: var(--neutral-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--neutral-color_2);
	--button-border-color-default: var(--neutral-color_5);

	--button-background-color-hover: var(--neutral-color_10);
	--button-text-color-hover: var(--neutral-color_2);
	--button-border-color-hover: var(--neutral-color_3);

	--button-background-color-active: var(--neutral-color_8);
	--button-text-color-active: var(--neutral-color_0);
	--button-border-color-active: var(--neutral-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--neutral-color_5);
	--button-border-color-disabled: var(--neutral-color_8);
}
%button_text_default_secondary {
	--button-outline-color: var(--neutral-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--neutral-color_2);
	--button-border-color-default: var(--neutral-color_5);

	--button-background-color-hover: var(--neutral-color_10);
	--button-text-color-hover: var(--neutral-color_2);
	--button-border-color-hover: var(--neutral-color_3);

	--button-background-color-active: var(--neutral-color_8);
	--button-text-color-active: var(--neutral-color_0);
	--button-border-color-active: var(--neutral-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--neutral-color_5);
	--button-border-color-disabled: var(--neutral-color_8);
}

// Успех
%button_filled_success_primary {
	--button-outline-color: var(--positive-color_4);

	--button-background-color-default: var(--positive-color_1);
	--button-text-color-default: var(--color-white);
	--button-border-color-default: var(--positive-color_2);

	--button-background-color-hover: var(--positive-color_0);
	--button-text-color-hover: var(--color-white);
	--button-border-color-hover: var(--positive-color_3);

	--button-background-color-active: var(--positive-color_-1);
	--button-text-color-active: var(--color-white);
	--button-border-color-active: var(--positive-color_1);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: var(--positive-color_4);
	--button-text-color-disabled: var(--global-white);
	--button-border-color-disabled: var(--positive-color_6);
}
%button_bordered_success_primary {
	--button-outline-color: var(--positive-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--positive-color_0);
	--button-border-color-default: var(--positive-color_5);

	--button-background-color-hover: var(--positive-color_10);
	--button-text-color-hover: var(--positive-color_0);
	--button-border-color-hover: var(--positive-color_3);

	--button-background-color-active: var(--positive-color_8);
	--button-text-color-active: var(--positive-color_-1);
	--button-border-color-active: var(--positive-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--positive-color_5);
	--button-border-color-disabled: var(--positive-color_8);
}
%button_text_success_primary {
	--button-outline-color: var(--positive-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--positive-color_0);
	--button-border-color-default: var(--positive-color_5);

	--button-background-color-hover: var(--positive-color_10);
	--button-text-color-hover: var(--positive-color_0);
	--button-border-color-hover: var(--positive-color_3);

	--button-background-color-active: var(--positive-color_8);
	--button-text-color-active: var(--positive-color_-1);
	--button-border-color-active: var(--positive-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--positive-color_5);
	--button-border-color-disabled: var(--positive-color_8);
}

%button_filled_success_secondary {
	--button-outline-color: var(--positive-color_4);

	--button-background-color-default: var(--positive-color_8);
	--button-text-color-default: var(--positive-color_1);
	--button-border-color-default: var(--positive-color_4);

	--button-background-color-hover: var(--positive-color_7);
	--button-text-color-hover: var(--positive-color_1);
	--button-border-color-hover: var(--positive-color_3);

	--button-background-color-active: var(--positive-color_6);
	--button-text-color-active: var(--positive-color_0);
	--button-border-color-active: var(--positive-color_2);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: var(--positive-color_10);
	--button-text-color-disabled: var(--positive-color_4);
	--button-border-color-disabled: var(--positive-color_6);
}
%button_bordered_success_secondary {
	--button-outline-color: var(--positive-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--positive-color_2);
	--button-border-color-default: var(--positive-color_5);

	--button-background-color-hover: var(--positive-color_10);
	--button-text-color-hover: var(--positive-color_2);
	--button-border-color-hover: var(--positive-color_3);

	--button-background-color-active: var(--positive-color_8);
	--button-text-color-active: var(--positive-color_0);
	--button-border-color-active: var(--positive-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--positive-color_5);
	--button-border-color-disabled: var(--positive-color_8);
}
%button_text_success_secondary {
	--button-outline-color: var(--positive-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--positive-color_2);
	--button-border-color-default: var(--positive-color_5);

	--button-background-color-hover: var(--positive-color_10);
	--button-text-color-hover: var(--positive-color_2);
	--button-border-color-hover: var(--positive-color_3);

	--button-background-color-active: var(--positive-color_8);
	--button-text-color-active: var(--positive-color_0);
	--button-border-color-active: var(--positive-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--positive-color_5);
	--button-border-color-disabled: var(--positive-color_8);
}

// Опасная
%button_filled_danger_primary {
	--button-outline-color: var(--negative-color_4);

	--button-background-color-default: var(--negative-color_1);
	--button-text-color-default: var(--color-white);
	--button-border-color-default: var(--negative-color_2);

	--button-background-color-hover: var(--negative-color_0);
	--button-text-color-hover: var(--color-white);
	--button-border-color-hover: var(--negative-color_3);

	--button-background-color-active: var(--negative-color_-1);
	--button-text-color-active: var(--color-white);
	--button-border-color-active: var(--negative-color_1);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: var(--negative-color_4);
	--button-text-color-disabled: var(--global-white);
	--button-border-color-disabled: var(--negative-color_6);
}
%button_bordered_danger_primary {
	--button-outline-color: var(--negative-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--negative-color_0);
	--button-border-color-default: var(--negative-color_5);

	--button-background-color-hover: var(--negative-color_10);
	--button-text-color-hover: var(--negative-color_0);
	--button-border-color-hover: var(--negative-color_3);

	--button-background-color-active: var(--negative-color_8);
	--button-text-color-active: var(--negative-color_-1);
	--button-border-color-active: var(--negative-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--negative-color_5);
	--button-border-color-disabled: var(--negative-color_8);
}
%button_text_danger_primary {
	--button-outline-color: var(--negative-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--negative-color_0);
	--button-border-color-default: var(--negative-color_5);

	--button-background-color-hover: var(--negative-color_10);
	--button-text-color-hover: var(--negative-color_0);
	--button-border-color-hover: var(--negative-color_3);

	--button-background-color-active: var(--negative-color_8);
	--button-text-color-active: var(--negative-color_-1);
	--button-border-color-active: var(--negative-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--negative-color_5);
	--button-border-color-disabled: var(--negative-color_8);
}

%button_filled_danger_secondary {
	--button-outline-color: var(--negative-color_4);

	--button-background-color-default: var(--negative-color_8);
	--button-text-color-default: var(--negative-color_1);
	--button-border-color-default: var(--negative-color_4);

	--button-background-color-hover: var(--negative-color_7);
	--button-text-color-hover: var(--negative-color_1);
	--button-border-color-hover: var(--negative-color_3);

	--button-background-color-active: var(--negative-color_6);
	--button-text-color-active: var(--negative-color_0);
	--button-border-color-active: var(--negative-color_2);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: var(--negative-color_10);
	--button-text-color-disabled: var(--negative-color_4);
	--button-border-color-disabled: var(--negative-color_6);
}
%button_bordered_danger_secondary {
	--button-outline-color: var(--negative-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--negative-color_2);
	--button-border-color-default: var(--negative-color_5);

	--button-background-color-hover: var(--negative-color_10);
	--button-text-color-hover: var(--negative-color_2);
	--button-border-color-hover: var(--negative-color_3);

	--button-background-color-active: var(--negative-color_8);
	--button-text-color-active: var(--negative-color_0);
	--button-border-color-active: var(--negative-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--negative-color_5);
	--button-border-color-disabled: var(--negative-color_8);
}
%button_text_danger_secondary {
	--button-outline-color: var(--negative-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--negative-color_2);
	--button-border-color-default: var(--negative-color_5);

	--button-background-color-hover: var(--negative-color_10);
	--button-text-color-hover: var(--negative-color_2);
	--button-border-color-hover: var(--negative-color_3);

	--button-background-color-active: var(--negative-color_8);
	--button-text-color-active: var(--negative-color_0);
	--button-border-color-active: var(--negative-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--negative-color_5);
	--button-border-color-disabled: var(--negative-color_8);
}

// Предупреждение
%button_filled_warning_primary {
	--button-outline-color: var(--notice-color_4);

	--button-background-color-default: var(--notice-color_1);
	--button-text-color-default: var(--color-white);
	--button-border-color-default: var(--notice-color_2);

	--button-background-color-hover: var(--notice-color_0);
	--button-text-color-hover: var(--color-white);
	--button-border-color-hover: var(--notice-color_3);

	--button-background-color-active: var(--notice-color_-1);
	--button-text-color-active: var(--color-white);
	--button-border-color-active: var(--notice-color_1);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: var(--notice-color_4);
	--button-text-color-disabled: var(--global-white);
	--button-border-color-disabled: var(--notice-color_6);
}
%button_bordered_warning_primary {
	--button-outline-color: var(--notice-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--notice-color_0);
	--button-border-color-default: var(--notice-color_5);

	--button-background-color-hover: var(--notice-color_10);
	--button-text-color-hover: var(--notice-color_0);
	--button-border-color-hover: var(--notice-color_3);

	--button-background-color-active: var(--notice-color_8);
	--button-text-color-active: var(--notice-color_-1);
	--button-border-color-active: var(--notice-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--notice-color_5);
	--button-border-color-disabled: var(--notice-color_8);
}
%button_text_warning_primary {
	--button-outline-color: var(--notice-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--notice-color_0);
	--button-border-color-default: var(--notice-color_5);

	--button-background-color-hover: var(--notice-color_10);
	--button-text-color-hover: var(--notice-color_0);
	--button-border-color-hover: var(--notice-color_3);

	--button-background-color-active: var(--notice-color_8);
	--button-text-color-active: var(--notice-color_-1);
	--button-border-color-active: var(--notice-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--notice-color_5);
	--button-border-color-disabled: var(--notice-color_8);
}

%button_filled_warning_secondary {
	--button-outline-color: var(--notice-color_4);

	--button-background-color-default: var(--notice-color_8);
	--button-text-color-default: var(--notice-color_1);
	--button-border-color-default: var(--notice-color_4);

	--button-background-color-hover: var(--notice-color_7);
	--button-text-color-hover: var(--notice-color_1);
	--button-border-color-hover: var(--notice-color_3);

	--button-background-color-active: var(--notice-color_6);
	--button-text-color-active: var(--notice-color_0);
	--button-border-color-active: var(--notice-color_2);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: var(--notice-color_10);
	--button-text-color-disabled: var(--notice-color_4);
	--button-border-color-disabled: var(--notice-color_6);
}
%button_bordered_warning_secondary {
	--button-outline-color: var(--notice-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--notice-color_2);
	--button-border-color-default: var(--notice-color_5);

	--button-background-color-hover: var(--notice-color_10);
	--button-text-color-hover: var(--notice-color_2);
	--button-border-color-hover: var(--notice-color_3);

	--button-background-color-active: var(--notice-color_8);
	--button-text-color-active: var(--notice-color_0);
	--button-border-color-active: var(--notice-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--notice-color_5);
	--button-border-color-disabled: var(--notice-color_8);
}
%button_text_warning_secondary {
	--button-outline-color: var(--notice-color_4);

	--button-background-color-default: rgba(255,255,255,0);
	--button-text-color-default: var(--notice-color_2);
	--button-border-color-default: var(--notice-color_5);

	--button-background-color-hover: var(--notice-color_10);
	--button-text-color-hover: var(--notice-color_2);
	--button-border-color-hover: var(--notice-color_3);

	--button-background-color-active: var(--notice-color_8);
	--button-text-color-active: var(--notice-color_0);
	--button-border-color-active: var(--notice-color_0);

	--button-background-color-focus: var(--button-background-color-hover);
	--button-text-color-focus: var(--button-text-color-hover);
	--button-border-color-focus: var(--button-border-color-hover);

	--button-background-color-disabled: rgba(255,255,255,0);
	--button-text-color-disabled: var(--notice-color_5);
	--button-border-color-disabled: var(--notice-color_8);
}

.c-button {
	$p: &;

	--button-font-family: var(--font-family-primary);
	--button-font-weight: var(--font-weight-medium);

	position: relative;

	display: inline-flex;
	justify-content: center;
	align-items: center;

	line-height: var(--button-line-height);
	white-space: nowrap;
	cursor: pointer;

	text-align: center;
	box-sizing: border-box;
	outline: none;

	transition: background-color, color, border-color;

	transition-duration: var(--transition-duration);
	transition-timing-function: var(--transition-timing-function);

	font-family: var(--button-font-family);
	font-weight: var(--button-font-weight);

	user-select: none;
	vertical-align: middle;
	-webkit-appearance: none;

	height: var(--button-height);
	font-size: var(--button-font-size);
	border-radius: var(--button-border-radius);
	padding: var(--button-padding-vertical) var(--button-padding-horizontal);

	&:hover {
		outline: none;
	}
	&:focus {
		outline: 1px solid var(--button-outline-color);
		outline-offset: 1px;
	}

	&:active {
		outline: none;
	}

	&:disabled,
	&[disabled],
	&.is-disabled {
		cursor: not-allowed;
	}

	> .icon {
		svg {
			--size: var(--button-line-height);
		}
	}

	> span {
		display: inline-flex;
		align-items: center;

		margin-left: var(--button-horizontal-gap);
		margin-right: var(--button-horizontal-gap);

		&:first-child {
			margin-left: 0;
		}
		&:last-child {
			margin-right: 0;
		}

	}



	& + & {
		margin-left: getMargin('control');
	}

	&::-moz-focus-inner {
		border: 0;
	}


	@extend %button_size_M;
	@extend %button_style_filled;

	&#{$p}--large {
		@extend %button_size_L;
	}
	&#{$p}--medium {
		@extend %button_size_M;
	}
	&#{$p}--small {
		@extend %button_size_S;
	}
	&#{$p}--extra-small {
		@extend %button_size_XS;
	}
	&#{$p}--extra-extra-small {
		@extend %button_size_XXS;
	}

	&#{$p}--default {
		&#{$p}--primary {
			&#{$p}--filled {
				@extend %button_filled_default_primary;
			}
			&#{$p}--bordered {
				@extend %button_bordered_default_primary;
			}
			&#{$p}--text {
				@extend %button_text_default_primary;
			}
		}
		&#{$p}--secondary {
			&#{$p}--filled {
				@extend %button_filled_default_secondary;
			}
			&#{$p}--bordered {
				@extend %button_bordered_default_secondary;
			}
			&#{$p}--text {
				@extend %button_text_default_secondary;
			}
		}
	}

	&#{$p}--accent {
		&#{$p}--primary {
			&#{$p}--filled {
				@extend %button_filled_accent_primary;
			}
			&#{$p}--bordered {
				@extend %button_bordered_accent_primary;
			}
			&#{$p}--text {
				@extend %button_text_accent_primary;
			}
		}
		&#{$p}--secondary {
			&#{$p}--filled {
				@extend %button_filled_accent_secondary;
			}
			&#{$p}--bordered {
				@extend %button_bordered_accent_secondary;
			}
			&#{$p}--text {
				@extend %button_text_accent_secondary;
			}
		}
	}

	&#{$p}--success {
		&#{$p}--primary {
			&#{$p}--filled {
				@extend %button_filled_success_primary;
			}
			&#{$p}--bordered {
				@extend %button_bordered_success_primary;
			}
			&#{$p}--text {
				@extend %button_text_success_primary;
			}
		}
		&#{$p}--secondary {
			&#{$p}--filled {
				@extend %button_filled_success_secondary;
			}
			&#{$p}--bordered {
				@extend %button_bordered_success_secondary;
			}
			&#{$p}--text {
				@extend %button_text_success_secondary;
			}
		}
	}

	&#{$p}--danger {
		&#{$p}--primary {
			&#{$p}--filled {
				@extend %button_filled_danger_primary;
			}
			&#{$p}--bordered {
				@extend %button_bordered_danger_primary;
			}
			&#{$p}--text {
				@extend %button_text_danger_primary;
			}
		}
		&#{$p}--secondary {
			&#{$p}--filled {
				@extend %button_filled_danger_secondary;
			}
			&#{$p}--bordered {
				@extend %button_bordered_danger_secondary;
			}
			&#{$p}--text {
				@extend %button_text_danger_secondary;
			}
		}
	}

	&#{$p}--warning {
		&#{$p}--primary {
			&#{$p}--filled {
				@extend %button_filled_warning_primary;
			}
			&#{$p}--bordered {
				@extend %button_bordered_warning_primary;
			}
			&#{$p}--text {
				@extend %button_text_warning_primary;
			}
		}
		&#{$p}--secondary {
			&#{$p}--filled {
				@extend %button_filled_warning_secondary;
			}
			&#{$p}--bordered {
				@extend %button_bordered_warning_secondary;
			}
			&#{$p}--text {
				@extend %button_text_warning_secondary;
			}
		}
	}


	&#{$p}--filled {
		@extend %button_style_filled;
	}
	&#{$p}--bordered {
		@extend %button_style_bordered;
	}
	&#{$p}--text {
		@extend %button_style_text;
	}
}
</style>
